import { useState } from "react";
import { motion } from "motion/react";
import {
  CheckCircle,
  ArrowLeft,
  Loader2,
  AlertCircle,
  User,
  Shield,
  Home,
  ChevronRight,
  Building,
  Settings,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "./ui/dialog";
import {
  loginAdmin,
  registerConsumer,
  RegisterConsumerRequest,
} from "../api/greenEnergy";

interface CredentialData {
  status?: string;
  credentialData?: {
    credential?: {
      credentialSubject?: {
        customerProfile?: {
          customerNumber?: string;
          consumptionProfiles?: Array<{
            meterId?: string;
            sanctionedLoad?: { value?: number; unit?: string };
          }>;
        };
        customerDetails?: {
          fullName?: string;
          installationAddress?: {
            address?: {
              streetAddress?: string;
              addressLocality?: string;
              addressRegion?: string;
              postalCode?: string;
              addressCountry?: string;
            };
          };
        };
      };
    };
  };
}

interface GreenEnergyCredentialProps {
  data: CredentialData | null;
  error: string | null;
  loading: boolean;
}

function extractMeterNumber(meterId?: string): string {
  if (!meterId) return "—";
  const parts = meterId.split(":");
  return parts[parts.length - 1] || meterId;
}

function DetailSection({
  icon,
  title,
  children,
}: {
  icon: React.ReactNode;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      <div className="bg-blue-50/80 px-5 py-3 flex items-center gap-2 border-b border-gray-200">
        {icon}
        <h3 className="text-sm font-semibold text-blue-800">{title}</h3>
      </div>
      <div className="p-5">{children}</div>
    </div>
  );
}

export function GreenEnergyCredential({
  data,
  error,
  loading,
}: GreenEnergyCredentialProps) {
  const [isRegistering, setIsRegistering] = useState(false);
  const [registrationStatus, setRegistrationStatus] = useState<
    "idle" | "success" | "error"
  >("idle");
  const [registrationMessage, setRegistrationMessage] = useState<string | null>(
    null
  );
  const [consentAccepted, setConsentAccepted] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center space-y-4"
        >
          <Loader2 className="w-12 h-12 text-emerald-500 animate-spin mx-auto" />
          <p className="text-lg text-gray-600">Decrypting credential...</p>
        </motion.div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50">
        <header className="bg-white border-b border-gray-200">
          <div className="max-w-5xl mx-auto px-6 py-4">
            <a
              href="/"
              className="flex items-center gap-2 text-gray-600 hover:text-emerald-600 transition-colors text-sm"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Home
            </a>
          </div>
        </header>
        <div className="max-w-5xl mx-auto px-6 py-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-2xl mx-auto text-center space-y-6"
          >
            <div className="w-16 h-16 rounded-full bg-red-50 flex items-center justify-center mx-auto">
              <AlertCircle className="w-8 h-8 text-red-500" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900">
              Unable to retrieve credential
            </h1>
            <p className="text-gray-600">
              We could not process the Green Energy credential. Please try
              again.
            </p>
            {error && (
              <div className="p-4 bg-red-50 border border-red-200 rounded-xl text-sm text-red-600 text-left">
                {error}
              </div>
            )}
            <a
              href="/"
              className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-emerald-500 to-cyan-500 text-white rounded-lg hover:shadow-xl transition-shadow"
            >
              Try Again
            </a>
          </motion.div>
        </div>
      </div>
    );
  }

  const credential = data?.credentialData?.credential;
  const customerDetails = credential?.credentialSubject?.customerDetails;
  const customerProfile = credential?.credentialSubject?.customerProfile;
  const address = customerDetails?.installationAddress?.address;
  const consumptionProfile = customerProfile?.consumptionProfiles?.[0];

  const customerName = customerDetails?.fullName || "";
  const consumerNumber = customerProfile?.customerNumber || "";
  const meterNumber = extractMeterNumber(consumptionProfile?.meterId);
  const sanctionedLoadValue = consumptionProfile?.sanctionedLoad?.value || 0;
  const sanctionedLoadUnit = consumptionProfile?.sanctionedLoad?.unit || "";
  const streetAddress = address?.streetAddress || "";
  const city = address?.addressLocality || "";
  const state = address?.addressRegion || "";
  const pincode = address?.postalCode || "";
  const fullAddress = [streetAddress, city, state, address?.addressCountry]
    .filter(Boolean)
    .join(", ");

  const validateRequiredFields = (): string | null => {
    if (!customerName) return "Customer name is missing from credential data.";
    if (!consumerNumber) return "Consumer number is missing from credential data.";
    if (!meterNumber || meterNumber === "—") return "Meter number is missing from credential data.";
    if (!sanctionedLoadValue) return "Sanctioned load is missing from credential data.";
    if (!streetAddress) return "Street address is missing from credential data.";
    if (!city) return "City is missing from credential data.";
    if (!state) return "State is missing from credential data.";
    if (!pincode) return "Pincode is missing from credential data.";
    return null;
  };

  const handleRegister = async () => {
    if (!consentAccepted) return;

    const validationError = validateRequiredFields();
    if (validationError) {
      setRegistrationStatus("error");
      setRegistrationMessage(validationError);
      return;
    }

    setIsRegistering(true);
    setRegistrationStatus("idle");
    setRegistrationMessage(null);

    try {
      const loginResponse = await loginAdmin();
      const token = loginResponse?.data?.tokens?.access?.token;

      if (!token) {
        throw new Error("Authentication failed. No access token received.");
      }

      const payload: RegisterConsumerRequest = {
        user: {
          name: customerName,
          phone: "9999999999",
          email: "consumer@gec.com",
          role: "consumer",
          companyId: "6a7894cf61c4cbb31decbd06",
          password: "Test@1234",
        },
        request: [
          {
            customer: {
              consumerNo: consumerNumber,
              type: "industrial",
              subCategory: "single_shift",
              substation: "SUBA1234",
              loadCategory: "HT",
              location: { state, city, pincode, address: streetAddress },
              capacity: sanctionedLoadValue,
              documents: [],
            },
            meter: [
              {
                meterSerialNo: meterNumber,
                type: "gross",
                phase: "3",
                installationDate: "2024-01-15",
                unit: "kWh",
                frequency: "30",
              },
            ],
            energyRequest: {
              energyUnits: 12000,
              pricePerUnit: 6.5,
              thresholdValue: 100,
              thresholdType: "percentage",
              contractType: "PPA",
              startDate: "2026-01-01",
              endDate: "2029-02-15",
              description: "Long term renewable energy requirement",
              isVoluntary: true,
              rpoPercentage: 15,
            },
          },
        ],
      };

      const regResponse = await registerConsumer(token, payload);
      setRegistrationStatus("success");
      setRegistrationMessage(
        regResponse?.message || "Consumer registered successfully."
      );
      setShowSuccessModal(true);
    } catch (err: any) {
      const message =
        err.response?.data?.message ||
        err.message ||
        "Something went wrong during registration.";
      setRegistrationStatus("error");
      setRegistrationMessage(message);
    } finally {
      setIsRegistering(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header / Breadcrumb */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-5xl mx-auto px-6 py-4">
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <a href="/" className="text-gray-400 hover:text-gray-600 transition-colors">
              <Home className="w-4 h-4" />
            </a>
            <ChevronRight className="w-3 h-3 text-gray-300" />
            <span className="text-gray-500">Onboarding</span>
            <ChevronRight className="w-3 h-3 text-gray-300" />
            <span className="text-gray-500">Consent callback</span>
            <ChevronRight className="w-3 h-3 text-gray-300" />
            <span className="text-emerald-600 font-medium">Review</span>
          </div>
        </div>
      </header>

      {/* Content */}
      <div className="max-w-5xl mx-auto px-6 py-8 md:py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          {/* Page Title */}
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6">
            Consumer Energy Passport — Review &amp; Onboard
          </h1>

          {/* Verification Banner */}
          <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 mb-8 flex items-start gap-3">
            <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0 mt-0.5">
              <Shield className="w-4 h-4 text-emerald-600" />
            </div>
            <div>
              <p className="text-sm font-semibold text-emerald-800">
                Verified consumer details received from PGVCL DISCOM
              </p>
              <p className="text-xs text-gray-500 mt-0.5">
                Fetched after consumer consent • Read-only data
              </p>
            </div>
          </div>

          {/* Consumer Details Card */}
          <div className="bg-white border border-gray-200 rounded-xl overflow-hidden mb-6">
            {/* Card Title */}
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">
                Consumer details
              </h2>
            </div>

            <div className="p-6 space-y-6">
              {/* Section 1: Consumer Identity */}
              <DetailSection
                icon={<User className="w-4 h-4 text-blue-600" />}
                title="Consumer identity"
              >
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-6 gap-y-3">
                  <span className="text-xs text-gray-500">Name</span>
                  <span className="text-sm font-medium text-gray-900">{customerName || "—"}</span>
                  <span className="text-xs text-gray-500">Consumer No.</span>
                  <span className="text-sm font-medium text-gray-900">{consumerNumber || "—"}</span>

                  <span className="text-xs text-gray-500">Load category</span>
                  <span className="text-sm font-medium text-gray-900">HT</span>
                  <span className="text-xs text-gray-500">Type</span>
                  <span className="text-sm font-medium text-gray-900">industrial</span>

                  <span />
                  <span />
                  <span className="text-xs text-gray-500">Sub-category</span>
                  <span className="text-sm font-medium text-gray-900">single_shift</span>
                </div>
              </DetailSection>

              {/* Section 2: Premise & Connection */}
              <DetailSection
                icon={<Building className="w-4 h-4 text-blue-600" />}
                title="Premise & connection"
              >
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-6 gap-y-3">
                  <span className="text-xs text-gray-500">Address</span>
                  <span className="text-sm font-medium text-gray-900">{fullAddress || "—"}</span>
                  <span className="text-xs text-gray-500">Pincode</span>
                  <span className="text-sm font-medium text-gray-900">{pincode || "—"}</span>

                  <span className="text-xs text-gray-500">City</span>
                  <span className="text-sm font-medium text-gray-900">{city || "—"}</span>
                  <span className="text-xs text-gray-500">Substation</span>
                  <span className="text-sm font-medium text-gray-900">SUBA1234</span>

                  <span className="text-xs text-gray-500">Contract load</span>
                  <span className="text-sm font-medium text-gray-900">
                    {sanctionedLoadValue ? `${sanctionedLoadValue} ${sanctionedLoadUnit}` : "—"}
                  </span>
                  <span />
                  <span />
                </div>
              </DetailSection>

              {/* Section 3: Meter & Energy Profile */}
              <DetailSection
                icon={<Settings className="w-4 h-4 text-blue-600" />}
                title="Meter & energy profile"
              >
                <div className="grid grid-cols-2 sm:grid-cols-6 gap-x-6 gap-y-3">
                  <span className="text-xs text-gray-500">Meter serial</span>
                  <span className="text-sm font-medium text-gray-900">{meterNumber}</span>
                  <span className="text-xs text-gray-500">Meter type</span>
                  <span className="text-sm font-medium text-gray-900">Gross</span>
                  <span />
                  <span />

                  <span className="text-xs text-gray-500">Interval</span>
                  <span className="text-sm font-medium text-gray-900">30 min</span>
                  <span className="text-xs text-gray-500">Unit</span>
                  <span className="text-sm font-medium text-gray-900">kWh</span>
                  <span className="text-xs text-gray-500">Phase</span>
                  <span className="text-sm font-medium text-gray-900">3 phase</span>
                </div>
              </DetailSection>
            </div>

            {/* Verified Footer Strip */}
            <div className="bg-emerald-50/80 border-t border-emerald-200 px-6 py-3 flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-emerald-600 flex-shrink-0" />
              <p className="text-xs text-gray-600">
                <span className="font-medium text-gray-700">Issued by PGVCL DISCOM</span>
                {" • Consent granted 20 Aug 2026 • Read-only verified data"}
              </p>
            </div>
          </div>

          {/* Information Message */}
          <p className="text-sm text-gray-500 text-center mb-6">
            Please verify the information before creating the consumer profile.
          </p>

          {/* Consent Section */}
          <div className="bg-white border border-gray-200 rounded-xl p-6 mb-8">
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={consentAccepted}
                onChange={(e) => setConsentAccepted(e.target.checked)}
                className="mt-1 w-4 h-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500 cursor-pointer"
              />
              <div>
                <p className="text-sm text-gray-700">
                  I accept the{" "}
                  <span className="text-emerald-600 font-medium underline">
                    Privacy Policy
                  </span>{" "}
                  and authorize OCT to create my consumer profile using the
                  consented DISCOM data.
                </p>
                <p className="text-xs text-gray-400 mt-1.5">
                  One-time consent • No standing access • DISCOM remains the
                  authoritative source
                </p>
              </div>
            </label>
          </div>

          {/* Registration Result */}
          {registrationStatus === "success" && (
            <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-xl mb-6 flex items-start gap-3">
              <CheckCircle className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-emerald-800">
                  Consumer Registered Successfully
                </p>
                <p className="text-sm text-emerald-600">{registrationMessage}</p>
              </div>
            </div>
          )}

          {registrationStatus === "error" && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-xl mb-6 flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-red-800">
                  Registration Failed
                </p>
                <p className="text-sm text-red-600">{registrationMessage}</p>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a
              href="/"
              className="w-full sm:w-auto px-8 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-center font-medium"
            >
              Cancel
            </a>
            <button
              onClick={handleRegister}
              disabled={!consentAccepted || isRegistering || registrationStatus === "success"}
              className="w-full sm:w-auto px-8 py-3 bg-emerald-600 text-white rounded-lg font-medium transition-all disabled:opacity-40 disabled:cursor-not-allowed hover:bg-emerald-700 flex items-center justify-center gap-2"
            >
              {isRegistering ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Onboarding...
                </>
              ) : (
                "Confirm & onboard"
              )}
            </button>
          </div>

          {/* Helper text when button is disabled */}
          {!consentAccepted && registrationStatus !== "success" && (
            <p className="text-xs text-gray-400 text-center mt-3">
              Accept the Privacy Policy to continue.
            </p>
          )}

          {/* Success Modal */}
          <Dialog open={showSuccessModal} onOpenChange={setShowSuccessModal}>
            <DialogContent className="!sm:max-w-sm">
              <DialogHeader className="text-center items-center">
                <div className="w-14 h-14 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="w-7 h-7 text-emerald-600" />
                </div>
                <DialogTitle className="text-center text-xl">
                  Successfully Onboarded
                </DialogTitle>
                <DialogDescription className="text-center">
                  Your consumer profile has been created successfully. Click OK to
                  continue to the homepage.
                </DialogDescription>
              </DialogHeader>
              <DialogFooter>
                <button
                  onClick={() =>
                    (window.location.href =
                      "https://gec-dev-consumer.energymarketplace.in/auth/login")
                  }
                  className="px-8 py-2.5 bg-emerald-600 text-white rounded-lg font-medium hover:bg-emerald-700 transition-colors"
                >
                  OK
                </button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </motion.div>
      </div>
    </div>
  );
}
